import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { trackEvent } from "@/lib/api";
import { useLang } from "@/i18n";
import { useEmailSend, emailedKey } from "@/hooks/useEmailSend";

export const EmailPopup = ({ tripId }) => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const sentRef = useRef(false);
  const { email, setEmail, consent, setConsent, busy, submit } = useEmailSend(tripId, "popup");
  const [before, after] = t("Accetto la {link} e acconsento all'invio dell'itinerario e delle comunicazioni relative a TripSaver.").split("{link}");

  useEffect(() => {
    if (localStorage.getItem(emailedKey(tripId))) return undefined;
    const id = setTimeout(() => { setOpen(true); trackEvent(tripId, "popup_shown"); }, 1500);
    return () => clearTimeout(id);
  }, [tripId]);

  const close = () => {
    setOpen(false);
    if (!sentRef.current) trackEvent(tripId, "popup_closed");
  };
  const onSent = () => {
    sentRef.current = true;
    setOpen(false);
    toast.success(t("E-mail inviata con successo!"));
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && close()}>
      <DialogContent data-testid="email-popup" className="no-print max-h-[90dvh] w-[calc(100%-2rem)] max-w-md overflow-y-auto rounded-3xl border-emerald-100 p-8">
        <span className="grid h-12 w-12 place-items-center rounded-2xl bg-emerald-50 text-emerald-700"><Mail className="h-6 w-6" /></span>
        <DialogTitle data-testid="email-popup-title" className="font-display text-2xl font-semibold text-gray-900">{t("Ricevilo gratis via mail")}</DialogTitle>
        <DialogDescription className="text-sm text-gray-600">{t("Salva la tua proposta e il TS Impact Score direttamente nella tua casella di posta per non perderli.")}</DialogDescription>
        <form onSubmit={(e) => submit(e, onSent)} noValidate className="mt-2 space-y-4">
          <input data-testid="email-popup-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("La tua email")} autoComplete="email"
            className="h-12 w-full rounded-full border border-gray-200 bg-slate-50 px-5 text-sm outline-none transition-colors focus:border-emerald-500 focus:bg-white" />
          <label className="flex cursor-pointer items-start gap-3 text-sm text-gray-600">
            <input data-testid="email-popup-consent-checkbox" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 accent-emerald-600" />
            <span>{before}<Link data-testid="email-popup-privacy-link" to="/privacy" target="_blank" className="font-semibold text-emerald-700 underline">{t("Privacy Policy")}</Link>{after}</span>
          </label>
          <button data-testid="email-popup-submit-button" type="submit" disabled={busy}
            className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-full bg-emerald-600 px-6 text-sm font-semibold text-white transition-colors hover:bg-emerald-500 disabled:opacity-70">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}{busy ? t("Invio in corso…") : t("Invia Gratis")}
          </button>
          <button data-testid="email-popup-skip-button" type="button" onClick={close}
            className="w-full text-center text-sm font-semibold text-gray-500 underline-offset-4 transition-colors hover:text-gray-800 hover:underline">
            {t("Continua senza e-mail")}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};
